import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Modal from '@components/ui/Modal/Modal';
import AlertModal from '@components/ui/AlertModal/AlertModal';
import { Button } from '@components/ui';
import Select from '@workpay/ds-shared/components/ui/Select';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import { dsCrossOrgSnapshotService } from '@services/dsCrossOrgSnapshotService';
import { getDocumentTypes } from '@services/documentTypeService';
import { useTranslation } from '@hooks/useTranslation';
import { toTitleCase } from '@utils/textCase';
import type { DocumentSnapshot } from '@workpay/ds-shared/types/snapshot';
import styles from './TemplatePickerModal.module.scss';

export interface TemplatePickerEmployeeContext {
  /**
   * Tenant organization id — matches the `organizationId` column on
   * `document_snapshot`. The picker's cross-org snapshot search filters by
   * this so only snapshots owned by the employee's tenant surface, never
   * workpay master templates.
   */
  organizationId?: string;
  countryId?: string;
  legalEntityId?: string;
  employmentTypeId?: string;
}

interface TemplatePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selected: DocumentSnapshot[]) => void;
  employeeContext?: TemplatePickerEmployeeContext;
  /** Snapshot IDs already added to the envelope — hidden from the picker. */
  excludeIds?: string[];
}

export function TemplatePickerModal({
  isOpen,
  onClose,
  onConfirm,
  employeeContext,
  excludeIds = [],
}: TemplatePickerModalProps) {
  const { t } = useTranslation();
  const [selectedDocTypeId, setSelectedDocTypeId] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pendingConfirm, setPendingConfirm] = useState(false);

  // Stable filter object — derived from employee context + doc type filter.
  // Status uses the FilterSpecification `in` operator (comma-separated string)
  // so a single query returns both ACTIVE and IN_REVIEW snapshots; DRAFT and
  // INACTIVE stay hidden. `organizationId` scopes to the tenant.
  const filters = useMemo(
    () => ({
      status: { in: 'ACTIVE,IN_REVIEW' },
      ...(employeeContext?.organizationId
        ? { organizationId: employeeContext.organizationId }
        : {}),
      ...(employeeContext?.countryId ? { countryId: employeeContext.countryId } : {}),
      ...(employeeContext?.legalEntityId ? { legalEntityId: employeeContext.legalEntityId } : {}),
      ...(employeeContext?.employmentTypeId
        ? { employmentTypeId: employeeContext.employmentTypeId }
        : {}),
      ...(selectedDocTypeId ? { documentTypeId: selectedDocTypeId } : {}),
    }),
    [employeeContext, selectedDocTypeId],
  );

  // Gate the search on organizationId being resolved — without it the snapshot
  // endpoint would return cross-tenant data, which is both wasteful and a
  // correctness leak for workpay admins generating per-employee contracts.
  // Either the employee is tenant-scoped (organizationId present) or the caller
  // explicitly opened the picker without employee context (admin-mode, allowed).
  const isTenantScoped = !!employeeContext;
  const hasTenantId = !!employeeContext?.organizationId;
  const shouldQuery = isOpen && (!isTenantScoped || hasTenantId);

  const { data: templatesPage, isLoading: templatesLoading, error: templatesError } = useQuery({
    queryKey: ['dsSnapshotsPicker', filters],
    queryFn: () =>
      dsCrossOrgSnapshotService.search(
        { filters },
        { page: 0, size: 100, sort: 'name,asc' },
      ),
    enabled: shouldQuery,
  });
  const templatesForbidden = isForbiddenError(templatesError);

  const { data: docTypesPage } = useQuery({
    queryKey: ['documentTypesForPicker'],
    queryFn: () => getDocumentTypes({ page: 0, size: 100, sort: 'name,asc' }),
    enabled: isOpen,
  });

  const docTypes = docTypesPage?.content ?? [];
  const docTypeNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const dt of docTypes) m.set(dt.id, dt.name);
    return m;
  }, [docTypes]);

  const templates = useMemo(() => {
    const list = templatesPage?.content ?? [];
    const excluded = new Set(excludeIds);
    return list.filter((tpl) => !excluded.has(tpl.id));
  }, [templatesPage, excludeIds]);

  const inReviewSelected = useMemo(
    () => templates.filter((tpl) => selectedIds.has(tpl.id) && tpl.status === 'IN_REVIEW'),
    [templates, selectedIds],
  );

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allVisibleSelected = templates.length > 0 && templates.every((tpl) => selectedIds.has(tpl.id));

  const toggleAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(templates.map((tpl) => tpl.id)));
    }
  };

  const proceedConfirm = () => {
    const selected = templates.filter((tpl) => selectedIds.has(tpl.id));
    onConfirm(selected);
    setSelectedIds(new Set());
    setSelectedDocTypeId('');
    setPendingConfirm(false);
    onClose();
  };

  const handleConfirm = () => {
    if (inReviewSelected.length > 0) {
      setPendingConfirm(true);
      return;
    }
    proceedConfirm();
  };

  const handleClose = () => {
    setSelectedIds(new Set());
    setSelectedDocTypeId('');
    setPendingConfirm(false);
    onClose();
  };

  const docTypeOptions = useMemo(
    () => [
      { value: '', label: t('K_DS_FILTER_DOCUMENT_TYPE_ALL', 'All Document Types') },
      ...docTypes.map((dt) => ({ value: dt.id, label: dt.name })),
    ],
    [docTypes, t],
  );

  const inReviewConfirmDescription =
    inReviewSelected.length === 1 ? (
      t(
        'K_DS_PICKER_CONFIRM_IN_REVIEW_SINGLE',
        '"{name}" is still in review. If reviewers request changes, you may need to regenerate this signing request. Do you want to continue?',
      ).replace('{name}', inReviewSelected[0]?.name ?? '')
    ) : (
      <>
        <p style={{ margin: 0 }}>
          {t(
            'K_DS_PICKER_CONFIRM_IN_REVIEW_MULTIPLE',
            'The following {count} templates are still in review. If reviewers request changes, you may need to regenerate this signing request.',
          ).replace('{count}', String(inReviewSelected.length))}
        </p>
        <ul className={styles.inReviewList}>
          {inReviewSelected.map((tpl) => (
            <li key={tpl.id}>{tpl.name}</li>
          ))}
        </ul>
        <p style={{ margin: 0 }}>
          {t('K_DS_PICKER_CONFIRM_IN_REVIEW_PROMPT', 'Do you want to continue?')}
        </p>
      </>
    );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('K_DS_PICKER_TITLE', 'Pick Templates')}
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={handleClose}>
              {t('K_CANCEL', 'Cancel')}
            </Button>
            <Button variant="filled" onClick={handleConfirm} disabled={selectedIds.size === 0}>
              {t('K_DS_PICKER_USE_SELECTED', 'Use Selected Templates')}
              {selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
            </Button>
          </>
        }
      >
        <div className={styles.filterBar}>
          <div className={styles.filterLabel}>{t('K_DS_FILTER_DOCUMENT_TYPE', 'Document Type')}</div>
          <div className={styles.filterSelect}>
            <Select
              options={docTypeOptions}
              value={selectedDocTypeId}
              onChange={(v) => setSelectedDocTypeId(v)}
              placeholder={t('K_DS_FILTER_DOCUMENT_TYPE_ALL', 'All Document Types')}
            />
          </div>
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.checkCol}>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAll}
                    aria-label={t('K_LABEL_SELECT_ALL', 'Select all')}
                  />
                </th>
                <th>{t('K_DS_COLUMN_TEMPLATE_NAME', 'Name')}</th>
                <th>{t('K_DS_COLUMN_DOCUMENT_TYPE', 'Document Type')}</th>
                <th>{t('K_DS_COLUMN_STATUS', 'Status')}</th>
                <th>{t('K_DS_COLUMN_FORMAT', 'Format')}</th>
                <th>{t('K_DS_COLUMN_LAST_UPDATED', 'Last Updated')}</th>
              </tr>
            </thead>
            <tbody>
              {isTenantScoped && !hasTenantId && (
                <tr>
                  <td colSpan={6} className={styles.loading}>
                    {t('K_DS_PICKER_RESOLVING_TENANT', 'Resolving employee’s client…')}
                  </td>
                </tr>
              )}
              {templatesLoading && (
                <tr>
                  <td colSpan={6} className={styles.loading}>
                    {t('K_DS_PICKER_LOADING', 'Loading templates...')}
                  </td>
                </tr>
              )}
              {!templatesLoading && templatesForbidden && (
                <tr>
                  <td colSpan={6}>
                    <Forbidden variant="empty-state" hideAction />
                  </td>
                </tr>
              )}
              {!templatesLoading && !templatesForbidden && hasTenantId && templates.length === 0 && (
                <tr>
                  <td colSpan={6} className={styles.empty}>
                    {t(
                      'K_DS_PICKER_EMPTY',
                      'No templates match this employee’s classification.',
                    )}
                  </td>
                </tr>
              )}
              {templates.map((tpl) => {
                const selected = selectedIds.has(tpl.id);
                const docTypeName = tpl.documentTypeId
                  ? docTypeNameById.get(tpl.documentTypeId) ?? '—'
                  : '—';
                const statusClass =
                  tpl.status === 'ACTIVE'
                    ? styles.badgeSuccess
                    : tpl.status === 'IN_REVIEW'
                      ? styles.badgeInfo
                      : styles.badgeNeutral;
                const statusLabel =
                  tpl.status === 'IN_REVIEW'
                    ? t('K_DS_STATUS_IN_REVIEW', 'In Review')
                    : toTitleCase(tpl.status);
                return (
                  <tr
                    key={tpl.id}
                    className={selected ? styles.rowSelected : ''}
                    onClick={() => toggleOne(tpl.id)}
                  >
                    <td className={styles.checkCol}>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleOne(tpl.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className={styles.nameCell}>{tpl.name}</td>
                    <td>{docTypeName}</td>
                    <td>
                      <span className={`${styles.badge} ${statusClass}`}>{statusLabel}</span>
                    </td>
                    <td>
                      <span
                        className={`${styles.formatBadge} ${
                          tpl.templateFormat === 'DOCX' ? styles.formatDocx : styles.formatPdf
                        }`}
                      >
                        {tpl.templateFormat}
                      </span>
                    </td>
                    <td>{tpl.updatedOn ? new Date(tpl.updatedOn).toLocaleDateString() : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Modal>

      <AlertModal
        variant="warning"
        isOpen={pendingConfirm}
        onClose={() => setPendingConfirm(false)}
        title={t('K_DS_PICKER_CONFIRM_IN_REVIEW_TITLE', 'Some templates are still in review')}
        description={inReviewConfirmDescription}
        primaryLabel={t('K_DS_PICKER_CONFIRM_IN_REVIEW_PROCEED', 'Continue Anyway')}
        onPrimaryClick={proceedConfirm}
        secondaryLabel={t('K_CANCEL', 'Cancel')}
      />
    </>
  );
}
